import React, { useMemo } from 'react'

import {
  Button,
  DataTable,
  DataTableColumn,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectWrapper,
  TextField,
  Typography
} from '@barracuda-internal/bds-core'
import { Block } from '@barracuda-internal/bds-core/dist/Icons/Core'
import { Warning } from '@barracuda-internal/bds-core/dist/Icons/Feedback'
import { useAppSelector } from 'redux/toolkit/hooks'

import inboundSettingsStyles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import {
  GEO_SELECT_NAMES,
  useGeoPoliciesTableLogic,
  UseGeoPoliciesTableLogicProps
} from 'components/pages/inboundSettings/regionalPolicies/useGeoPoliciesTableLogic'
import { TableText } from 'components/libs/layout/pages/TableText'
import { GeoPolicy, GeoPolicyStatus } from 'types/Settings'
import { Cell } from 'components/libs/grid/cell'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import { getCountryListCode3 } from 'lib/isoCountries'
import { FormSpacer, PageSubtitle } from 'components/libs/layout/pages/pageLayout'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'

const BASE_I18N_KEY = 'ess.inbound_settings.regional_policies'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'
const DIALOG_I18N_KEY = 'ess.dialog'

const GeoPoliciesTable = (props: UseGeoPoliciesTableLogicProps) => {
  const isReadonly = useIsReadonly()
  const inboundClasses = inboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)
  const formatDialogText = useFormatMessage(DIALOG_I18N_KEY)
  const language = useAppSelector(state => state.app.language)

  const [state, eventHandlers] = useGeoPoliciesTableLogic(props)

  // Get country list and lookup map - re-compute when language changes for i18n
  const langCode = language?.substring(0, 2) || 'en'
  const { countryList, countryNameMap } = useMemo(() => {
    const list = getCountryListCode3(langCode)
    const nameMap = new Map(list.map(c => [c.code, c.name]))
    return { countryList: list, countryNameMap: nameMap }
  }, [langCode])

  return useMemo(
    () => (
      <Grid container>
        <PageSubtitle>{formatMessage('sender_policies')}</PageSubtitle>
        <FormSpacer />
        <Grid container>
          <Typography variant="h3">{formatMessage('geo_policies')}</Typography>
          <Button
            className={inboundClasses.bulkEdit}
            onClick={eventHandlers.onBulkEdit}
            variant="contained"
            size="small"
            data-testid="bulk-button"
            disabled={isReadonly}
          >
            {formatInboundMessage('bulk_edit')}
          </Button>
        </Grid>
        <DataTable
          sortable={{
            allowUnsort: false,
            mode: 'single'
          }}
          sort={state.sort}
          onSortChange={eventHandlers.handleSortChange}
          data={state.tableData}
        >
          <DataTableColumn
            sortable
            field="country"
            title={formatMessage('country')}
            cell={({ dataItem }: { dataItem: GeoPolicy }) => (
              <Cell>
                {dataItem.id === '' && (
                  <SelectWrapper variant="outlined" error={!!state.error}>
                    <Select
                      variant="outlined"
                      className={inboundClasses.mediumDropdown}
                      name={GEO_SELECT_NAMES.COUNTRY}
                      defaultValue={state.formState.country}
                      onChange={eventHandlers.handleOnSelectChange}
                      SelectDisplayProps={{ style: { padding: 8 } }}
                      data-testid="new-country-field"
                      error={!!state.error}
                      disabled={isReadonly}
                    >
                      {countryList.map(countryData => (
                        <MenuItem key={countryData.code} value={countryData.code}>
                          {countryData.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{state.error && formatMessage(state.error)}</FormHelperText>
                  </SelectWrapper>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="country-field">
                    {countryNameMap.get(dataItem.country) || dataItem.country}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="policy"
            title={formatMessage('policy')}
            cell={({ dataItem }: { dataItem: GeoPolicy }) => (
              <Cell>
                {dataItem.id === '' && (
                  <Select
                    variant="outlined"
                    className={inboundClasses.smallDropdown}
                    name={GEO_SELECT_NAMES.POLICY}
                    defaultValue={state.formState.policy}
                    onChange={eventHandlers.handleOnSelectChange}
                    SelectDisplayProps={{ style: { padding: 8 } }}
                    data-testid="new-policy-field"
                    disabled={isReadonly}
                  >
                    {state.policyOptions.map(policyValue => (
                      <MenuItem key={policyValue} value={policyValue}>
                        {formatMessage(policyValue)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="policy-field" className={inboundClasses.flexWrapper}>
                    {dataItem.policy === GeoPolicyStatus.block && <Block className={inboundClasses.block} />}
                    {dataItem.policy === GeoPolicyStatus.quarantine && (
                      <Warning className={inboundClasses.quarantine} />
                    )}
                    {formatMessage(dataItem.policy as string)}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="comment"
            title={formatMessage('comment')}
            cell={({ dataItem }: { dataItem: GeoPolicy }) => (
              <Cell data-testid="comment-cell" className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <TextField
                    fullWidth
                    name="comment"
                    defaultValue={state.formState.comment}
                    inputRef={state.form.fields.comment.ref}
                    onChange={eventHandlers.handleOnInputChange}
                    inputProps={{ style: { padding: 8 } }}
                    data-testid="new-comment-field"
                    disabled={isReadonly}
                  />
                )}
                {dataItem.id && <TableText dataText={dataItem.comment} truncated dataTestId="comment-field" />}
              </Cell>
            )}
          />
          <DataTableColumn
            field="actions"
            title={formatMessage('actions')}
            cell={({ dataItem }: { dataItem: GeoPolicy }) => (
              <Cell data-testid="action-cell" className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onAdd()
                    }}
                    variant="text"
                    data-testid="add-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('add')}
                  </Button>
                )}
                {dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onRemove(dataItem.id)
                    }}
                    variant="text"
                    data-testid="remove-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('remove')}
                  </Button>
                )}
              </Cell>
            )}
          />
        </DataTable>
        {state.isConfirmDialogOpen && (
          <WarningDialog
            title={formatDialogText('regional_policies.title')}
            description={formatDialogText('regional_policies.description')}
            onCancel={eventHandlers.onRemoveCancel}
            onConfirm={eventHandlers.onRemoveConfirm}
            cancelButtonLabel={formatDialogText('regional_policies.cancel')}
            confirmButtonLabel={formatDialogText('regional_policies.confirm')}
          />
        )}
      </Grid>
    ),
    [
      formatMessage,
      formatInboundMessage,
      formatDialogText,
      inboundClasses.bulkEdit,
      inboundClasses.mediumDropdown,
      inboundClasses.smallDropdown,
      inboundClasses.flexWrapper,
      inboundClasses.block,
      inboundClasses.quarantine,
      inboundClasses.smallCell,
      eventHandlers,
      isReadonly,
      state.sort,
      state.tableData,
      state.isConfirmDialogOpen,
      state.error,
      state.formState.country,
      state.formState.policy,
      state.formState.comment,
      state.policyOptions,
      state.form.fields.comment.ref,
      countryList,
      countryNameMap
    ]
  )
}

export default GeoPoliciesTable
